import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { useSelector } from 'react-redux';
import { REPORT_FILE_TYPE } from '../../types/report.type';
import { generateReportDescription, generateFilterChips } from '../../utilis/report-description';

const ReportSummary = ({ 
    reportOptions, 
    pageType, 
    title, 
    selectedColumns = [], 
    tableFilterType,
    showTitle = true,
    compact = false,
    cardType
}) => {
    const headerFilterItems = useSelector((state) => state.common.headerFilterItems);

    // Generate description using shared utility
    const description = React.useMemo(() => 
        generateReportDescription({
            reportOptions,
            pageType,
            title,
            headerFilterItems
        }),
        [reportOptions, pageType, title, headerFilterItems]
    );

    // Get filter chips using shared utility
    const filterChips = React.useMemo(() => 
        generateFilterChips(headerFilterItems, cardType),
        [headerFilterItems, cardType]
    );

    if (compact) {
        return (
            <Box sx={{ p: 1, backgroundColor: '#f8f9fa', borderRadius: 1, border: '1px solid rgba(0,0,0,0.1)' }}>
                <Typography variant="body2" sx={{ color: '#333', fontWeight: 500 }}>
                    {description}
                </Typography>
                {filterChips.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                        {filterChips.slice(0, 3).map((chip) => (
                            <Chip
                                key={chip.key}
                                label={chip.label}
                                size="small"
                                sx={{
                                    height: '20px',
                                    fontSize: '0.7rem',
                                    backgroundColor: chip.isLocked ? '#fff8e1' : '#f5f5f5',
                                    border: chip.isLocked ? '1px solid #ffc107' : '1px solid #e0e0e0',
                                    color: chip.isLocked ? '#ff8f00' : '#666',
                                    fontWeight: chip.isLocked ? 600 : 400,
                                    '& .MuiChip-label': {
                                        px: 0.5
                                    }
                                }}
                            />
                        ))}
                        {filterChips.length > 3 && (
                            <Typography variant="caption" sx={{ color: '#666', alignSelf: 'center' }}>
                                +{filterChips.length - 3} more
                            </Typography>
                        )}
                    </Box>
                )}
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2, border: '1px solid rgba(0,0,0,0.1)' }}>
            {showTitle && (
                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 'bold', color: '#1976d2', display: 'flex', alignItems: 'center', gap: 1 }}>
                    📊 Report Configuration Summary
                </Typography>
            )}
            
            <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ color: '#333', lineHeight: 1.6, fontWeight: 500 }}>
                    {description}
                </Typography>
            </Box>
            
            {/* Filter Chips Display */}
            {filterChips.length > 0 && (
                <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" sx={{ color: '#666', fontWeight: 'bold', mb: 1, display: 'block' }}>
                        Applied Filters:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {filterChips.map((chip) => (
                            <Chip
                                key={chip.key}
                                label={chip.label}
                                size="small"
                                sx={{
                                    backgroundColor: chip.isLocked ? '#fff8e1' : '#f0f7ff',
                                    border: chip.isLocked ? '2px solid #ffc107' : '1px solid #1976d2',
                                    color: chip.isLocked ? '#e68900' : '#1976d2',
                                    fontWeight: chip.isLocked ? 600 : 500,
                                    fontSize: '0.75rem',
                                    height: 'auto',
                                    py: 0.5,
                                    '& .MuiChip-label': {
                                        px: 1,
                                        py: 0.25,
                                        whiteSpace: 'normal',
                                        overflow: 'visible',
                                        textOverflow: 'unset'
                                    },
                                    // Golden glow effect for locked items
                                    ...(chip.isLocked && {
                                        boxShadow: '0 0 4px rgba(255, 193, 7, 0.4)',
                                        '&:hover': {
                                            boxShadow: '0 0 8px rgba(255, 193, 7, 0.6)'
                                        }
                                    })
                                }}
                            />
                        ))}
                    </Box>
                </Box>
            )}

            {/* Additional Options */}
            {(reportOptions?.reportFileType === REPORT_FILE_TYPE.EXCEL && selectedColumns.length > 0) ||
             (tableFilterType && tableFilterType !== "all") ||
             reportOptions?.isSendReportSeparate ? (
                <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" sx={{ color: '#666', fontWeight: 'bold', mb: 1, display: 'block' }}>
                        Additional Options:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {reportOptions?.reportFileType === REPORT_FILE_TYPE.EXCEL && selectedColumns.length > 0 && (
                            <Chip
                                label={`Custom Columns (${selectedColumns.length})`}
                                size="small"
                                sx={{ backgroundColor: '#e8f5e8', color: '#2e7d32', fontSize: '0.75rem' }}
                            />
                        )}
                        {tableFilterType && tableFilterType !== "all" && (
                            <Chip
                                label="Table Filter Applied"
                                size="small"
                                sx={{ backgroundColor: '#e8f5e8', color: '#2e7d32', fontSize: '0.75rem' }}
                            />
                        )}
                        {reportOptions?.isSendReportSeparate && (
                            <Chip
                                label="Separate by Facility"
                                size="small"
                                sx={{ backgroundColor: '#e8f5e8', color: '#2e7d32', fontSize: '0.75rem' }}
                            />
                        )}
                    </Box>
                </Box>
            ) : null}
        </Box>
    );
};

export default ReportSummary; 